import { useEffect, useState } from 'react';

const MethodHero = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (id: string) => {
    try {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error('scrollToSection error:', error);
    }
  };

  return (
    <section className="relative overflow-hidden bg-cream">
      {/* Split layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[80vh]">

        {/* Left: Light image panel */}
        <div
          className="relative flex items-center justify-center overflow-hidden h-[220px] sm:h-[300px] lg:h-auto transition-all duration-1000 delay-200"
          style={{ opacity: visible ? 1 : 0 }}
        >
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/b60e9237f2c37c11ae2b2462fc54d51f.png"
              alt="Vionne Stability Method - Facial Acupuncture"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-cream/40 via-transparent to-cream/20"></div>
          </div>

          {/* Badge */}
          <div className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 bg-white/50 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg">
            <p className="text-bronze text-[9px] font-sans tracking-[0.25em] uppercase font-semibold mb-0.5">
              Licensed Acupuncturist
            </p>
            <p className="text-charcoal text-[12px] sm:text-[13px] font-display italic font-light">
              CCHPBC Certified Practice.
            </p>
          </div>

          {/* Decorative circles */}
          <div className="absolute top-6 right-6 sm:top-10 sm:right-10 w-14 h-14 sm:w-20 sm:h-20 rounded-full border border-bronze/10"></div>
          <div className="absolute top-8 right-8 sm:top-12 sm:right-12 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-bronze/20"></div>
        </div>

        {/* Right: Dark editorial panel */}
        <div className="relative flex items-center bg-charcoal overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/5b77cb8b1f48ffb2ec3935b5cd073c4d.png"
              alt="Vionne Stability Method background"
              className="w-full h-full object-cover object-center opacity-35"
            />
            <div className="absolute inset-0" style={{ backgroundColor: 'rgba(36, 33, 30, 0.85)' }}></div>
          </div>

          <div
            className="relative z-10 px-8 sm:px-12 lg:px-16 py-10 sm:py-14 lg:py-16 w-full max-w-lg transition-all duration-700"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
          >
            {/* Label */}
            <div className="inline-flex items-center gap-2 mb-5 sm:mb-8">
              <div className="w-7 sm:w-10 h-[1px] bg-softGold/50"></div>
              <span className="text-softGold/70 text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">
                Clinical Framework · Botanical Acupuncture
              </span>
              <div className="w-7 sm:w-10 h-[1px] bg-softGold/50"></div>
            </div>

            {/* Title */}
            <h1 className="font-display text-3xl sm:text-4xl lg:text-[3.2rem] font-light text-white leading-[1.15] mb-5 sm:mb-8">
              <span className="italic">Vionne Stability</span>
              <br />
              <span className="text-softGold italic">Method</span>
            </h1>

            <div className="w-12 sm:w-16 h-[1px] bg-gradient-to-r from-softGold/60 to-transparent mb-5 sm:mb-8"></div>

            {/* Description */}
            <p className="text-white/65 text-[12px] sm:text-[13px] font-sans font-light leading-[1.8] sm:leading-[1.9] mb-4 sm:mb-6">
              Designed for women who see beauty as a long-term investment in consistency, balance, and holistic well-being.
              The Stability Method works from the foundation upward—instead of forcing change at the surface.
            </p>

            <p className="text-softGold/80 text-[11px] sm:text-[12px] font-sans font-light italic leading-relaxed mb-2 sm:mb-3">
              When underlying systems return to harmony, visible change follows naturally—calm, refined, and sustainable.
            </p>

            <p className="text-white/60 text-[13px] sm:text-[15px] font-sans font-semibold leading-relaxed mb-6 sm:mb-10">
              Because lasting change begins at the root.
            </p>

            {/* Stats row */}
            <div className="flex items-center gap-6 sm:gap-10 mb-6 sm:mb-10">
              {[
                { value: '3', label: 'Stability Pillars' },
                { value: '4', label: 'Regulatory Layers' },
                { value: '5', label: 'Facial Archetypes' },
                { value: '7+', label: 'Years Practice' },
              ].map((stat, i) => (
                <>
                  {i > 0 && <div key={`d-${i}`} className="w-[1px] h-8 sm:h-10 bg-white/15"></div>}
                  <div key={`s-${i}`}>
                    <p className="font-display text-2xl sm:text-3xl text-white font-light italic">{stat.value}</p>
                    <p className="text-white/40 text-[9px] sm:text-[10px] font-sans tracking-wider uppercase mt-0.5 sm:mt-1">{stat.label}</p>
                  </div>
                </>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-2 flex-nowrap">
              <a
                href="/booking"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-softGold text-white rounded-full text-[8px] font-sans font-semibold hover:bg-bronze transition-all duration-300 whitespace-nowrap cursor-pointer tracking-[0.2em] uppercase"
              >
                <span>Reserve Your Session</span>
                <i className="ri-arrow-right-line text-[9px]"></i>
              </a>
              <button
                onClick={() => scrollToSection('client-suitability')}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-transparent border border-white/25 text-white/80 rounded-full text-[8px] font-sans font-medium hover:border-softGold/60 hover:text-softGold transition-all duration-300 whitespace-nowrap cursor-pointer tracking-[0.2em] uppercase"
              >
                <span>Who This Is For</span>
                <i className="ri-arrow-down-line text-[9px]"></i>
              </button>
            </div>

            <div className="mt-8 flex items-center gap-3">
              <div className="w-[1px] h-6 sm:h-8 bg-gradient-to-b from-softGold/50 to-transparent"></div>
              <span className="text-white/35 text-[8px] sm:text-[9px] font-sans tracking-[0.25em] uppercase">
                Scroll to Explore
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Page Navigation Bar */}
      <div className="relative z-10 border-t border-[#D9CFC4] bg-white">
        <div className="max-w-[1280px] mx-auto px-2 sm:px-8 lg:px-12">
          <nav className="flex items-center gap-0">
            {[
              { label: 'Method Foundation', id: 'method-details', icon: 'ri-ancient-gate-line' },
              { label: 'Stability Outcome', id: 'clinical-observations', icon: 'ri-sparkling-line' },
              { label: 'Who This Is For', id: 'client-suitability', icon: 'ri-user-heart-line' },
              { label: 'Where To Begin', id: 'stability-pathway', icon: 'ri-map-pin-line' },
            ].map((item, i) => (
              <button
                key={i}
                onClick={() => scrollToSection(item.id)}
                className="group flex items-center gap-1 sm:gap-2 px-2 py-3 sm:px-5 sm:py-3.5 text-[8px] sm:text-[10px] font-sans font-semibold tracking-[0.1em] sm:tracking-[0.2em] uppercase text-[#4A4440] hover:text-[#B8A48A] transition-all duration-300 whitespace-nowrap cursor-pointer border-b-[1.5px] border-transparent hover:border-[#B8A48A] relative flex-1 justify-center"
              >
                <div className="hidden sm:flex w-3.5 h-3.5 items-center justify-center">
                  <i className={`${item.icon} text-[11px]`}></i>
                </div>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
    </section>
  );
};

export default MethodHero;
